import type { CSSProperties, ReactNode } from "react";
import { useRef, useState } from "react";
import { cn } from "../lib/utils";

export interface DockItem {
  id: string;
  label: string;
  icon: string | ReactNode;
  onClick?: () => void;
  badge?: string | number;
}

interface DockProps {
  items: DockItem[];
  maxScale?: number;
  className?: string;
  style?: CSSProperties;
}

export function Dock({
  items,
  maxScale = 1.6,
  className,
  style,
}: DockProps) {
  const dockRef = useRef<HTMLDivElement>(null);
  const [mouseX, setMouseX] = useState<number | null>(null);

  function onMouseMove(e: React.MouseEvent<HTMLDivElement>) {
    setMouseX(e.clientX);
  }

  function onMouseLeave() {
    setMouseX(null);
  }

  function getScale(el: HTMLElement | null): number {
    if (mouseX === null || !el || !dockRef.current) return 1;
    const rect = el.getBoundingClientRect();
    const center = rect.left + rect.width / 2;
    const dist = Math.abs(mouseX - center);
    const proximity = 100;
    const t = Math.max(0, 1 - dist / proximity);
    return 1 + (maxScale - 1) * t * t;
  }

  return (
    <div
      ref={dockRef}
      className={cn("dock", className)}
      style={style}
      onMouseMove={onMouseMove}
      onMouseLeave={onMouseLeave}
    >
      {items.map((item) => (
        <DockIcon key={item.id} item={item} getScale={getScale} />
      ))}
    </div>
  );
}

function DockIcon({
  item,
  getScale,
}: {
  item: DockItem;
  getScale: (el: HTMLElement | null) => number;
}) {
  const ref = useRef<HTMLButtonElement>(null);
  const [, force] = useState(0);

  const scale = getScale(ref.current);

  return (
    <button
      ref={ref}
      className="dock-item"
      style={{ transform: `scale(${scale}) translateY(${(scale - 1) * -20}%)` }}
      onClick={item.onClick}
      title={item.label}
      onMouseMove={() => force((n) => n + 1)}
    >
      <span className="dock-item-icon">{item.icon}</span>
      {item.badge != null && (
        <span className="dock-item-badge">{item.badge}</span>
      )}
      <span className="dock-item-label">{item.label}</span>
    </button>
  );
}
